import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'antd';
import {
    AuditOutlined,
    CalendarOutlined,
    CheckOutlined,
    CopyrightOutlined,
    EuroCircleOutlined,
    EuroOutlined,
    ScheduleOutlined,
} from '@ant-design/icons';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FieldInfo,
    FieldInfoAdvanced,
    LinkComponent,
    cardLayoutOptions,
} from '../../';
import { ApproveMonthlyOrderModalForm } from './ApproveMonthlyOrderModalForm';
import { PoNumberFlow } from './PoNumberFlow';
import { ShowStatus } from './ShowStatus';
import { ShowUsageProducts } from './ShowUsageProducts';
import { IFieldLayout, IIdSpinners, monthlyOrderStatuses } from '../../../interfaces';
import { useUiIsSpinnerFound } from '../../../hooks';
import { useMonthlyOrder } from '../../../hooks/stores/useMonthlyOrderStores';
import { postMonthlyMarkAsPaidById, postMonthlyOrderCancelById } from '../../../middleware';
import { dateText2Text, dateText2Utc } from '../../../instruments';
import { tB, tF } from '../../../translate';
import { getInvoiceStatusContent } from '../../Invoice/Components';
import CardStyles from '../../styles/m_itemCardStyles.less';
import Styles from '../m_styles.less';

export const MonthlyOrderView: React.FC<IIdSpinners> = observer(({ idSpinners = [] }: IIdSpinners) => {
    const layout: IFieldLayout = { labelCol: { flex: '0 1 170px' }, rowGutter: 8 };
    const [isModalApprove, setIsModalApprove] = useState<boolean>(false);

    const order = useMonthlyOrder();
    const isLoading = useUiIsSpinnerFound(idSpinners);
    const cancelMonthlyOrder = () => {
        if (order.id) {
            postMonthlyOrderCancelById(order.id.toString());
        }
    };
    const markAsPaidMonthlyOrder = () => {
        if (order.id) {
            postMonthlyMarkAsPaidById(order.id.toString());
        }
    };

    return (
        <Catcher>
            <CardMainLayout
                columnOption={cardLayoutOptions.cardInfoEdit}
                idSpinners={idSpinners}
                name={`${tF('Monthly order')} #${order.id}`}
                icon={<EuroOutlined />}
                OtherButton={
                    <div className={CardStyles.text}>
                        <ShowStatus status={order.status} />
                    </div>
                }
            >
                <FieldInfoAdvanced
                    icon={<CalendarOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Year Month'}
                >
                    {`${order.year}-${order.month}`}
                </FieldInfoAdvanced>

                <FieldInfoAdvanced
                    icon={<CopyrightOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Company'}
                >
                    <LinkComponent
                        currentListComponent={'BillingCompaniesList'}
                        id={order.companyId?.id}
                        name={order.companyId?.name}
                    />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<CopyrightOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Invoicing company'}
                >
                    <LinkComponent
                        currentListComponent={'BillingCompaniesList'}
                        id={order.invoicingCompany?.id}
                        name={order.invoicingCompany?.name}
                    />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<ScheduleOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Start Date'}
                >
                    {dateText2Utc(order.periodStart)}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<ScheduleOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'End Date'}
                >
                    {dateText2Utc(order.periodEnd)}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<CopyrightOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'PO number'}
                >
                    <PoNumberFlow />
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<EuroOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Amount'}
                >
                    {order.amount}
                </FieldInfoAdvanced>

                {order.dueDate ? (
                    <FieldInfoAdvanced
                        icon={<CalendarOutlined className={CardStyles.labelIcon} />}
                        layout={layout}
                        title={'Due Date'}
                    >
                        {order.dueDate}
                    </FieldInfoAdvanced>
                ) : null}

                {order.invoice ? (
                    <>
                        <FieldInfoAdvanced
                            icon={<AuditOutlined className={CardStyles.labelIcon} />}
                            layout={layout}
                            title={'Invoice'}
                        />
                        <div className={Styles.fieldMargin}>
                            <FieldInfo title={'ID'}>{order.invoice.id}</FieldInfo>
                            {order.invoice.sentAt ? (
                                <FieldInfo title={'Sent at'}>{dateText2Text(order.invoice.sentAt)}</FieldInfo>
                            ) : null}
                            <FieldInfo title={'Date'}>{dateText2Text(order.invoice.date)}</FieldInfo>
                            {order.invoice.fileUrl ? (
                                <FieldInfo title={'File'}>
                                    {order.invoice.fileUrl
                                        ? getInvoiceStatusContent(order.invoice.fileUrl, order.invoice.pdfFileStatus)
                                        : null}
                                </FieldInfo>
                            ) : null}
                            <FieldInfo title={'Status'}>
                                {order.invoice.pdfFileStatus ? order.invoice.pdfFileStatus.toUpperCase() : null}
                            </FieldInfo>
                        </div>
                    </>
                ) : null}

                <ShowUsageProducts isLoading={isLoading} usageProducts={order.usageProducts} />

                <FieldInfoAdvanced
                    icon={<CalendarOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Created at'}
                >
                    {dateText2Utc(order.createdAt)}
                </FieldInfoAdvanced>
                <FieldInfoAdvanced
                    icon={<CalendarOutlined className={CardStyles.labelIcon} />}
                    layout={layout}
                    title={'Updated at'}
                >
                    {dateText2Utc(order.updatedAt)}
                </FieldInfoAdvanced>
                {order.id && isModalApprove && (
                    <ApproveMonthlyOrderModalForm isOpen={isModalApprove} setIsOpen={setIsModalApprove} />
                )}
                <CardInfoToolBar
                    dataTest={'MonthlyOrderView'}
                    loadingTypes={idSpinners}
                    onCancel={order.status === monthlyOrderStatuses.DRAFT ? cancelMonthlyOrder : undefined}
                    onCancelButtonType={'default'}
                    onCancelDanger
                >
                    {order.id && order.status === monthlyOrderStatuses.DRAFT ? (
                        <Button
                            icon={<CheckOutlined className={CardStyles.greenIcon} />}
                            onClick={() => setIsModalApprove(true)}
                            disabled={isLoading}
                        >
                            {tB('Approve')}
                        </Button>
                    ) : null}
                    {order.id && order.status === monthlyOrderStatuses.APPROVED ? (
                        <Button
                            icon={<EuroCircleOutlined className={CardStyles.primaryIcon} />}
                            onClick={markAsPaidMonthlyOrder}
                            disabled={isLoading}
                        >
                            {tB('Mark as Paid')}
                        </Button>
                    ) : null}
                </CardInfoToolBar>
            </CardMainLayout>
        </Catcher>
    );
});
